import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import styles from './notifications-panel-content.module.scss';
import type {PanelContentProps} from '@Components/panel';
import type {NotificationSettingsPanelData} from '@Panels/notifications-panel/notifications-panel.types';
import {NotificationsPanelDisplayType} from '@Panels/notifications-panel/notifications-panel.types';
import {NotificationsSettings} from '@Panels/notifications-panel/components/notifications-settings/notifications-settings';
import {NotificationsList} from '@Panels/notifications-panel/components/notifications-list/notifications-list';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {setNotificationSettings, setPanelDisplayType, onNotificationsLazyLoadScroll} from '@Panels/notifications-panel/notifications-panel-slice';
import {
  useCanLoadMoreNotifications,
  useFetchLazyLoadedNotifications,
  useStartFetchingNotifications,
  useNewNotifications,
  useNotificationPanelState,
  useOtherNotifications,
  useTodaysNotifications,
  useYesterdaysNotifications,
} from '@Panels/notifications-panel/notifications-panel-hooks';
import {useAppDispatch} from '@/hooks';
import type {NotificationsPanelContentProps} from '@Panels/notifications-panel/components/notifications-panel-content/notifications-panel-content.types';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {GA4EventName, GA4EventParamName, NotificationsSettingsToggleSource} from '@Libraries/ga-events';
import {HEIGHT_FROM_BOTTOM} from '@Libraries/notifications-panel-library';
import {debounce} from '@Utils/misc.util';

export function NotificationsPanelContent(props: NotificationsPanelContentProps): ReactElement {
  const panelState = useNotificationPanelState();
  const otherNotifications = useOtherNotifications();
  const newNotifications = useNewNotifications();
  const todaysNotifications = useTodaysNotifications();
  const yesterdaysNotifications = useYesterdaysNotifications();
  const dispatch = useAppDispatch();
  const isMobile = props.isMobile;
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const panelDisplayState = useNotificationPanelState();
  const startFetchingNotifications = useStartFetchingNotifications();
  const loadMore = useCanLoadMoreNotifications();
  const fetchNotifications = useFetchLazyLoadedNotifications();
  const shadowHandler = (container: HTMLElement): void => {
    props.setHasShadow?.(container.scrollTop > 0);
  };

  useEffect(() => {
    if (startFetchingNotifications && loadMore) {
      fetchNotifications();
    }
  }, [startFetchingNotifications, loadMore]);
  const onScroll = (container: HTMLElement): void => {
    const {scrollTop, scrollHeight, clientHeight} = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= HEIGHT_FROM_BOTTOM;

    if (isNearBottom) {
      dispatch(onNotificationsLazyLoadScroll());
    }
    shadowHandler(container);
  };

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const debouncedScrollHandler = debounce(() => {
      onScroll(container);
    }, 150);

    container.addEventListener('scroll', debouncedScrollHandler, {passive: true});

    return (): void => {
      container.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, []);

  const getNotificationListItems = (): ReactElement<PanelContentProps> => {
    return (
      <div ref={listContainerRef} className={`${styles.verticalOverflow} ${styles.contentMaxHeight} ${props.className} ${styles.notificationsListSpacing}`}>
        <NotificationsList customClose={props.customClose} notifications={newNotifications} heading={window.i18next.t('pmwjs_new')} />
        <NotificationsList customClose={props.customClose} notifications={todaysNotifications} heading={window.i18next.t('pmwjs_today')} />
        <NotificationsList customClose={props.customClose} notifications={yesterdaysNotifications} heading={window.i18next.t('pmwjs_yesterday')} />
        <NotificationsList customClose={props.customClose} notifications={otherNotifications} heading={window.i18next.t('pmwjs_older')} />
        {startFetchingNotifications ? getNotificationsLoadingState(1) : null}
      </div>
    );
  };

  const onClickNotificationPlaceState = (): void => {
    if (panelDisplayState === NotificationsPanelDisplayType.TURN_ON_NOTIFICATIONS) {
      const updatedSettings: NotificationSettingsPanelData = {
        designUpdates: true,
        teamUpdates: true,
        emailCampaigns: true,
        socialMediaPosts: true,
        eventsUpdates: true,
      };

      dispatch(setNotificationSettings(updatedSettings));
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_TOGGLE, {[GA4EventParamName.VALUE]: NotificationsSettingsToggleSource.ALL});
    }
    dispatch(setPanelDisplayType(NotificationsPanelDisplayType.SETTINGS));
  };

  const getNotificationPlaceholderState = (heading: string, subheading: string, icon: string, buttonText: string): ReactElement<PanelContentProps> => {
    const spacingValues = isMobile ? 'spacing-p-t-4 spacing-p-b-6 spacing-p-r-4 spacing-p-l-4' : 'spacing-p-t-6 spacing-p-b-8 spacing-p-r-4 spacing-p-l-4';

    return (
      <div className={`flex-center flex-v-row ${spacingValues} ${props.className}`}>
        <Icon className="bg-neutral-3 spacing-p-4 spacing-m-b-2 radius-round" icon={icon} size={IconSize.SIZE_ICON_32} />
        <Text val={heading} className="content-body spacing-m-b-1" size={TextSize.XSMALL} bold />
        <Text val={subheading} className="content-body spacing-m-b-1" size={TextSize.XXSMALL} />
        <Button
          customClasses={isMobile ? 'spacing-m-t-4' : 'spacing-m-t-6'}
          text={buttonText}
          size={Size.SMALL}
          type={Type.SECONDARY}
          onClick={() => {
            onClickNotificationPlaceState();
          }}
          icon={panelDisplayState === NotificationsPanelDisplayType.TURN_ON_NOTIFICATIONS ? '' : 'icon-settings'}
        />
      </div>
    );
  };
  const getNotificationEmptyListState = (): ReactElement<PanelContentProps> => {
    return getNotificationPlaceholderState(
      window.i18next.t('pmwjs_no_new_notifications'),
      window.i18next.t('pmwjs_keep_you_updated'),
      'icon-notification',
      window.i18next.t('pmwjs_notifications_settings')
    );
  };

  const getTurnOnNotificationsState = (): ReactElement<PanelContentProps> => {
    return getNotificationPlaceholderState(
      window.i18next.t('pmwjs_notifications_turned_off'),
      window.i18next.t('pmwjs_notifications_turned_off_help'),
      'icon-notification-off',
      window.i18next.t('pmwjs_turn_on_notifications')
    );
  };

  const getNotificationsLoadingState = (index: number): ReactElement<PanelContentProps> => {
    return (
      <div key={index} className={`flexbox flex-h-row ${styles.rowGap} spacing-p-2`}>
        <SkeletonBrick className={`radius-4 ${styles.thumbnailSkeleton}`} />
        <div className={`_full-width flex-v-row ${styles.rowGap}`}>
          <SkeletonBrick className={styles.thumbnailSkeletonText} />
          <SkeletonBrick className={`${styles.thumbnailSkeletonText} ${styles.thumbnailSkeletonHalfWidth}`} />
        </div>
      </div>
    );
  };
  const getNotificationListItemLoadingState = (): ReactElement<PanelContentProps> => {
    return (
      <div className={`${styles.skeletonSpacing} ${styles.contentMaxHeight} flex-v-row ${styles.rowGapSkeleton} _overflow-hidden ${props.className}`}>
        {Array.from({length: 10}).map((_, index) => getNotificationsLoadingState(index))}
      </div>
    );
  };
  const getPanelContent = (): ReactElement<PanelContentProps> => {
    switch (panelState) {
      case NotificationsPanelDisplayType.EMPTY:
        return getNotificationEmptyListState();
      case NotificationsPanelDisplayType.LOADED:
        return getNotificationListItems();
      case NotificationsPanelDisplayType.LOADING:
        return getNotificationListItemLoadingState();
      case NotificationsPanelDisplayType.SETTINGS:
        return <NotificationsSettings className={props.className} />;
      case NotificationsPanelDisplayType.TURN_ON_NOTIFICATIONS:
        return getTurnOnNotificationsState();
      default:
        return getNotificationEmptyListState();
    }
  };

  return getPanelContent();
}
